import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import {
  getMockLootBox,
  WheelStatusEnum,
} from '../../../../helpers/fortuneWheel';
import { PrizeWheel } from '../PrizeWheel';
import { PrizeComp } from './components/Prize';
import { WheelButton } from './components/WheelButton';
import { FortuneWheelApi } from '@/api/cms';
import {
  Lootbox,
  LootboxItem,
  LootboxStage,
  playerLootboxActivation,
} from '@/api/common';
import { ImageComponent } from '@/components/Image';
import { delay } from '@/helpers/common';
import { Modals } from '@/helpers/modals';
import { useHandleBonus } from '@/hooks/bonuses';
import { useTimeoutRegistrationModal } from '@/hooks/timeoutRegistrationModal';
import { useFortuneWheel } from '@/modules/promotions/hooks/useFortuneWheel';
import { useServer } from '@/modules/server/context/ServerEnvContext';
import { useModalsStore } from '@/store';
import { useCommonStore } from '@/store/commonStore';

import { ScWheelArrow, ScWheelContainer, ScWheelPrizes } from './styled';

interface WheelProps {
  lootboxes: Lootbox[];
  arrowImg: string;
  bonusBuyPromocode?: string;
  wheelImages: FortuneWheelApi['wheel'];
}

export const Wheel: FC<WheelProps> = ({
  lootboxes,
  arrowImg,
  wheelImages,
  bonusBuyPromocode,
}) => {
  const { t } = useTranslation('wheel');

  const { isMobileDevice } = useServer();

  const {
    fetchLootboxes,
    isAuthenticated,
    user,
    pendingUser,
    pendingLootboxes,
  } = useCommonStore(
    useShallow((state) => ({
      fetchLootboxes: state.fetchLootboxes,
      isAuthenticated: state.isAuthenticated,
      user: state.user,
      pendingUser: state.pendingUser,
      pendingLootboxes: state.pendingLootboxes,
    }))
  );
  const open = useModalsStore.use.open();

  const code = (() => {
    if (!user || !bonusBuyPromocode) return null;

    return bonusBuyPromocode;
  })();

  const { handleBonus, pendingBonus } = useHandleBonus(code);

  const [winningPrizeIndex, setWinningPrizeIndex] = useState(0);
  const [lootboxItem, setLootboxItem] = useState<LootboxItem | null>(null);
  const [status, setStatus] = useState<WheelStatusEnum | null>(null);

  const { isDailySpinsLimit } = useFortuneWheel();

  const mockLootbox = useMemo(() => {
    return getMockLootBox(t);
  }, [t]);

  useEffect(() => {
    setWinningPrizeIndex(0);
    setLootboxItem(null);
    setStatus(null);
  }, [isAuthenticated]);

  const lootbox = useMemo(
    () =>
      (isAuthenticated &&
        lootboxes.find((lootbox) => lootbox.stage === LootboxStage.Issued)) ||
      null,
    [lootboxes, isAuthenticated]
  );

  const size = useMemo(() => (isMobileDevice ? 314 : 492), [isMobileDevice]);

  const handleSpin = useCallback(async () => {
    setStatus(WheelStatusEnum.Loading);
    const { data } = await playerLootboxActivation(lootbox!.id);

    const index = lootbox!.items.findIndex((item) => item.id === data!.id);
    setWinningPrizeIndex(index);

    setStatus(WheelStatusEnum.Spinning);

    await delay(7000);
    setLootboxItem(data!);

    await delay(2000);
    setStatus(WheelStatusEnum.Finished);

    fetchLootboxes();
  }, [lootbox, fetchLootboxes]);

  const handleClickWheelButton = useCallback(async () => {
    if (!isAuthenticated) {
      open(Modals.Registration);
      return;
    }

    if (!lootbox) {
      handleBonus();
      return;
    }

    await handleSpin();
  }, [isAuthenticated, lootbox, handleSpin, open, handleBonus]);

  useTimeoutRegistrationModal();

  const wheelImageByStatus = useMemo(() => {
    if (status === WheelStatusEnum.Finished || isDailySpinsLimit) return;

    if (status === WheelStatusEnum.Spinning) return wheelImages.spinning.url;
    if (lootbox) return wheelImages.active.url;

    if (!status || status === WheelStatusEnum.Loading)
      return wheelImages.finished.url;
  }, [status, wheelImages, lootbox, isDailySpinsLimit]);

  const isSpinning = useMemo(
    () => status === WheelStatusEnum.Spinning,
    [status]
  );

  return (
    <ScWheelContainer size={size}>
      <div className="inner">
        {wheelImageByStatus && (
          <ImageComponent
            className="wheelImage"
            src={wheelImageByStatus}
            width={isMobileDevice ? 556 : 832}
            height={isMobileDevice ? 556 : 832}
            alt="wheel image"
          />
        )}
        {status === WheelStatusEnum.Finished && lootboxItem ? (
          <PrizeWheel lootboxItem={lootboxItem} />
        ) : (
          <>
            <ScWheelPrizes
              isSpinning={isSpinning}
              totalPrizes={(lootbox || mockLootbox).items.length}
              winningPrizeIndex={winningPrizeIndex}
            >
              {(lootbox || mockLootbox).items.map((item, index) => (
                <PrizeComp
                  key={item.id}
                  index={index}
                  total={(lootbox || mockLootbox).items.length}
                  isHighlighted={lootboxItem?.id === item.id}
                  isLocked={!lootbox}
                  {...item}
                />
              ))}
            </ScWheelPrizes>
            <WheelButton
              invert={!lootbox}
              isLocked={!isSpinning && isDailySpinsLimit}
              isSpinning={isSpinning || isDailySpinsLimit}
              isLoading={
                status === WheelStatusEnum.Loading ||
                pendingBonus ||
                pendingUser ||
                pendingLootboxes
              }
              onClick={handleClickWheelButton}
            />
            <ScWheelArrow arrowImg={arrowImg} />
          </>
        )}
      </div>
    </ScWheelContainer>
  );
};
